import React, { useState, useEffect, useCallback, useMemo } from'react';
import { useTranslation } from'@/store/languageStore';
import { dashboardService, DashboardWidget, DashboardLayout, type AvailableWidget } from'@/services/dashboard/dashboardService';
import WidgetContainer from'@/components/dashboard/WidgetContainer';
import WidgetSelector from'@/components/dashboard/WidgetSelector';
import VehicleCountWidget from'@/components/dashboard/widgets/VehicleListWidget';
import InspectionDueWidget from'@/components/dashboard/widgets/InspectionDueWidget';
import LeasingExpiringWidget from'@/components/dashboard/widgets/LeasingExpiringWidget';
import TaxDueWidget from '@/components/dashboard/widgets/TaxDueWidget';
import UnpluggedWidget from'@/components/dashboard/widgets/UnpluggedWidget';
import LowBatteryWidget from'@/components/dashboard/widgets/LowBatteryWidget';
import VehicleStatusWidget from'@/components/dashboard/widgets/VehicleStatusWidget';
import ServiceDueWidget from'@/components/dashboard/widgets/ServiceDueWidget';
import ServiceCostWidget from'@/components/dashboard/widgets/ServiceCostWidget';
import ProductionCostWidget from '@/components/dashboard/widgets/ProductionCostWidget';
import MyAssignmentsWidget from'@/components/dashboard/widgets/MyAssignmentsWidget';
import MyBookingsWidget from'@/components/dashboard/widgets/MyBookingsWidget';
import GpsDataLagWidget from '@/components/dashboard/widgets/GpsDataLagWidget';
import DeviceHealthWidget from '@/components/dashboard/widgets/DeviceHealthWidget';
import ActionRequiredWidget from '@/components/dashboard/widgets/ActionRequiredWidget';
import MineWidget from '@/components/dashboard/widgets/MineWidget';
import FavoriteModulesBar from '@/components/dashboard/FavoriteModulesBar';
import DashboardGreeting from '@/components/dashboard/DashboardGreeting';
import { PlusIcon, Squares2X2Icon, PencilIcon, CheckIcon, XMarkIcon } from'@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable, DropResult } from'@hello-pangea/dnd';
import toast from'react-hot-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { getDashboardWidgetFeatureCode, GLOBAL_ADMIN_ONLY_DASHBOARD_WIDGETS } from '@/constants/dashboardWidgets';
import { useAuthStore } from '@/store/authStore';
import { isGlobalAdminUser } from '@/utils/isGlobalAdminUser';
import { isDesignPreviewHost } from '@/utils/designPreview';
import DashboardWidgetGrid from '@/components/dashboard/DashboardWidgetGrid';
import { defaultWidgetSize, normalizeDashboardWidgets, type ColSpan, type RowSpan } from '@/utils/dashboardGrid';
const widgetComponents: Record<string, React.FC<{ onRemove?: () => void }>> = {
'vehicle-count': VehicleCountWidget,
'inspection-due': InspectionDueWidget,
'leasing-expiring': LeasingExpiringWidget,
'tax-due': TaxDueWidget,
'unplugged-vehicles': UnpluggedWidget,
'low-battery': LowBatteryWidget,
'vehicle-status': VehicleStatusWidget,
'service-due': ServiceDueWidget,
'service-cost': ServiceCostWidget,
'production-cost': ProductionCostWidget,
'my-assignments': MyAssignmentsWidget,
'my-bookings': MyBookingsWidget,
'gps-data-lag': GpsDataLagWidget,
'device-health': DeviceHealthWidget,
'action-required': ActionRequiredWidget,
'mine': MineWidget,
};
const COLUMNS = [0, 1, 2] as const;
const PREVIEW_ADDABLE: AvailableWidget[] = [
  {
    type: 'action-required',
    name: 'Kräver åtgärd',
    nameEn: 'Needs action',
    description: 'Service, besiktning, leasing och fordonsskatt',
    descriptionEn: 'Service, inspection, leasing and vehicle tax',
    icon: 'wrench',
    defaultColumn: 0,
    implemented: true,
  },
  {
    type: 'production-cost',
    name: 'Produktion och kostnad',
    nameEn: 'Production and cost',
    description: 'Körda kilometer och kostnad per månad',
    descriptionEn: 'Kilometres and cost per month',
    icon: 'currency',
    defaultColumn: 0,
    implemented: true,
  },
  {
    type: 'mine',
    name: 'Mitt',
    nameEn: 'Mine',
    description: 'Egna bokningar och tilldelningar',
    descriptionEn: 'Your bookings and assignments',
    icon: 'clipboard-check',
    defaultColumn: 0,
    implemented: true,
  },
];
const REPLACED_ON_PREVIEW = [
  'low-battery', 'unplugged-vehicles', 'gps-data-lag',
  'service-due', 'inspection-due', 'leasing-expiring', 'tax-due',
  'my-assignments',
];
const Dashboard: React.FC = () => {
const { t } = useTranslation();
const user = useAuthStore((s) => s.user);
const designPreview = typeof window !== 'undefined' && isDesignPreviewHost(window.location.hostname);
const { hasActionAccess, isUnlimited, isLoading: permissionsLoading } = usePermissions();
const canShowWidget = (widgetType: string) => {
  if (GLOBAL_ADMIN_ONLY_DASHBOARD_WIDGETS.has(widgetType)) {
    return isGlobalAdminUser(user);
  }
  const featureCode = getDashboardWidgetFeatureCode(widgetType);
  if (!featureCode) return true;
  if (isUnlimited) return true;
  if (permissionsLoading) return false;
  return hasActionAccess(featureCode, 'VIEW');
};
const [layout, setLayout] = useState<DashboardLayout>({ widgets: [] });
const [draftLayout, setDraftLayout] = useState<DashboardLayout>({ widgets: [] });
const [loading, setLoading] = useState(true);
const [editMode, setEditMode] = useState(false);
const [selectorOpen, setSelectorOpen] = useState(false);
const [saving, setSaving] = useState(false);
const activeLayout = editMode ? draftLayout : layout;
useEffect(() => {
loadLayout();
}, []);
const loadLayout = async () => {
try {
setLoading(true);
const data = await dashboardService.getLayout();
setLayout(data);
} catch {
setLayout({ widgets: [] });
} finally {
setLoading(false);
}
};
const enterEditMode = () => {
const snapshot = JSON.parse(JSON.stringify(layout));
setDraftLayout(designPreview ? { widgets: normalizeDashboardWidgets(snapshot.widgets) } : snapshot);
setEditMode(true);
};
const cancelEditMode = () => {
setEditMode(false);
setDraftLayout({ widgets: [] });
};
const saveAndExit = async () => {
try {
setSaving(true);
await dashboardService.saveLayout(draftLayout);
setLayout(JSON.parse(JSON.stringify(draftLayout)));
setEditMode(false);
toast.success(t('dashboard.layoutSaved'));
} catch {
toast.error(t('dashboard.layoutSaveFailed'));
} finally {
setSaving(false);
}
};
const handleAddWidget = (widgetType: string, column: number) => {
const targetLayout = editMode ? draftLayout : layout;
const size = defaultWidgetSize(widgetType);
const newWidget: DashboardWidget = {
id: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
widgetType,
column: designPreview ? 0 : column,
order: designPreview ? targetLayout.widgets.length : targetLayout.widgets.filter(w => w.column === column).length,
...(designPreview ? { colSpan: size.colSpan, rowSpan: size.rowSpan } : {}),
};
const newLayout = {
...targetLayout,
widgets: [...targetLayout.widgets, newWidget],
};
if (editMode) {
setDraftLayout(newLayout);
} else {
setLayout(newLayout);
dashboardService.saveLayout(newLayout).catch(() => {
toast.error(t('dashboard.layoutSaveFailed'));
});
}
toast.success(t('dashboard.widgetAdded'));
};
const handleRemoveWidget = (widgetId: string) => {
if (editMode) {
setDraftLayout(prev => ({
...prev,
widgets: prev.widgets.filter(w => w.id !== widgetId),
}));
} else {
const newLayout = { ...layout, widgets: layout.widgets.filter(w => w.id !== widgetId) };
setLayout(newLayout);
dashboardService.saveLayout(newLayout).catch(() => {
toast.error(t('dashboard.layoutSaveFailed'));
});
toast.success(t('dashboard.widgetRemoved'));
}
};
const getWidgetsForColumn = useCallback((column: number) => {
return activeLayout.widgets
.filter(w => w.column === column)
.sort((a, b) => a.order - b.order);
}, [activeLayout]);
const handleResizeWidget = (widgetId: string, size: { colSpan?: ColSpan; rowSpan?: RowSpan }) => {
setDraftLayout(prev => ({
widgets: normalizeDashboardWidgets(prev.widgets).map(widget => (
widget.id === widgetId ? { ...widget, ...size } : widget
)),
}));
};
const gridWidgets = useMemo(
() => normalizeDashboardWidgets(activeLayout.widgets),
[activeLayout.widgets],
);
const renderWidgetBody = (widget: DashboardWidget) => {
const WidgetComponent = widgetComponents[widget.widgetType];
const allowed = canShowWidget(widget.widgetType);
if (!allowed) {
return (
<div className="text-textTertiary text-center py-8 text-sm">
{t('dashboard.widgetNoPermission')}
</div>
);
}
if (!WidgetComponent) {
return (
<div className="text-textTertiary text-center py-8">
{t('dashboard.widgetNotAvailable')}
</div>
);
}
return <WidgetComponent onRemove={() => handleRemoveWidget(widget.id)} />;
};
const handleDragEnd = (result: DropResult) => {
const { source, destination } = result;
if (!destination) return;
if (source.droppableId === 'dashboard-grid') {
const items = normalizeDashboardWidgets(draftLayout.widgets);
const [moved] = items.splice(source.index, 1);
if (!moved) return;
items.splice(destination.index, 0, moved);
items.forEach((widget, index) => { widget.order = index; });
setDraftLayout({ widgets: items });
return;
}
if (source.droppableId === destination.droppableId && source.index === destination.index) return;
const sourceCol = parseInt(source.droppableId.replace('column-',''));
const destCol = parseInt(destination.droppableId.replace('column-',''));
const allWidgets = [...draftLayout.widgets];
const sourceWidgets = allWidgets
.filter(w => w.column === sourceCol)
.sort((a, b) => a.order - b.order);
const [movedWidget] = sourceWidgets.splice(source.index, 1);
movedWidget.column = destCol;
if (sourceCol === destCol) {
sourceWidgets.splice(destination.index, 0, movedWidget);
sourceWidgets.forEach((w, i) => { w.order = i; });
} else {
const destWidgets = allWidgets
.filter(w => w.column === destCol && w.id !== movedWidget.id)
.sort((a, b) => a.order - b.order);
destWidgets.splice(destination.index, 0, movedWidget);
sourceWidgets.forEach((w, i) => { w.order = i; });
destWidgets.forEach((w, i) => { w.order = i; });
}
setDraftLayout({ widgets: allWidgets });
};
const renderWidget = (widget: DashboardWidget, index: number) => {
const WidgetComponent = widgetComponents[widget.widgetType];
const allowed = canShowWidget(widget.widgetType);
if (editMode) {
return (
<Draggable key={widget.id} draggableId={widget.id} index={index}>
{(provided, snapshot) => (
<div
ref={provided.innerRef}
{...provided.draggableProps}
className={`mb-4 transition-shadow ${snapshot.isDragging ?'shadow-2xl z-50' :''}`}
>
<WidgetContainer
editMode
onRemove={() => handleRemoveWidget(widget.id)}
dragHandleProps={provided.dragHandleProps ?? undefined}
>
{!allowed ? (
<div className="text-textTertiary text-center py-8 text-sm">
{t('dashboard.widgetNoPermission')}
</div>
) : WidgetComponent ? (
<WidgetComponent />
) : (
<div className="text-textTertiary text-center py-8">
{t('dashboard.widgetNotAvailable')}
</div>
)}
</WidgetContainer>
</div>
)}
</Draggable>
);
}
if (!allowed) {
return null;
}
return (
<WidgetContainer key={widget.id} onRemove={() => handleRemoveWidget(widget.id)}>
{WidgetComponent ? (
<WidgetComponent onRemove={() => handleRemoveWidget(widget.id)} />
) : (
<div className="text-textTertiary text-center py-8">
{t('dashboard.widgetNotAvailable')}
</div>
)}
</WidgetContainer>
);
};
const renderColumn = (column: number) => {
const widgets = getWidgetsForColumn(column);
if (editMode) {
return (
<Droppable droppableId={`column-${column}`} key={column}>
{(provided, snapshot) => (
<div
ref={provided.innerRef}
{...provided.droppableProps}
className={`space-y-0 min-h-[120px] rounded-xl p-2 transition-colors ${
snapshot.isDraggingOver
?'bg-blue-50/50 dark:bg-blue-900/10 ring-2 ring-blue-300 dark:ring-blue-700 ring-dashed'
:'bg-transparent'
}`}
>
{widgets.map((widget, index) => renderWidget(widget, index))}
{provided.placeholder}
{widgets.length === 0 && !snapshot.isDraggingOver && (
<div className="border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-xl p-8 text-center">
<span className="text-blue-400 dark:text-blue-500 text-sm">{t('dashboard.dragHere')}</span>
</div>
)}
<button
onClick={() => setSelectorOpen(true)}
className="w-full border-2 border-dashed border-borderDefault rounded-xl p-3 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/5 transition-colors mt-2"
>
<PlusIcon className="w-5 h-5 mx-auto text-textTertiary"/>
</button>
</div>
)}
</Droppable>
);
}
return (
<div className="space-y-6"key={column}>
{widgets.map((widget, index) => renderWidget(widget, index))}
{widgets.length === 0 && (
<div
className="border-2 border-dashed border-borderDefault rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/5 transition-colors"
onClick={() => { enterEditMode(); setSelectorOpen(true); }}
>
<PlusIcon className="w-8 h-8 mx-auto text-textTertiary mb-2"/>
<span className="text-textTertiary text-sm">{t('dashboard.addWidget')}</span>
</div>
)}
</div>
);
};
const renderEmptyDashboard = () => (
<div className="flex flex-col items-center justify-center h-[60vh] text-center">
<div className="p-6 rounded-full bg-bgElevated mb-6">
<Squares2X2Icon className="w-16 h-16 text-textTertiary"/>
</div>
<h2 className="text-2xl font-semibold text-textDefault mb-2">
{t('dashboard.emptyDashboard')}
</h2>
<p className="text-textSecondary mb-6 max-w-md">
{t('dashboard.emptyDashboardDescription')}
</p>
<button
onClick={() => { enterEditMode(); setSelectorOpen(true); }}
className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-lg"
>
<PlusIcon className="w-5 h-5"/>
{t('dashboard.addFirstWidget')}
</button>
</div>
);
if (loading) {
return (
<div className="flex items-center justify-center h-full">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
</div>
);
}
return (
<div className="dq-dashboard p-6 h-full overflow-auto bg-bgContainer">
<div className={designPreview ? 'dq-dash-top' : ''}>
<div className={designPreview ? 'dq-dash-greeting-row flex items-start justify-between gap-4' : 'flex items-center justify-between mb-6'}>
{editMode ? (
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<Squares2X2Icon className="w-6 h-6 text-primary"/>
{t('dashboard.editDashboard')}
</h1>
) : (
<DashboardGreeting firstName={user?.first_name} />
)}
<div className="flex items-center gap-2 shrink-0">
{editMode ? (
<>
{designPreview && (
<button
type="button"
onClick={() => setSelectorOpen(true)}
className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-blue-400 text-blue-700 dark:text-blue-300 rounded-lg font-medium transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
>
<PlusIcon className="w-5 h-5"/>
{t('dashboard.addWidget')}
</button>
)}
<button
onClick={cancelEditMode}
className="flex items-center gap-2 px-4 py-2 bg-bgBase hover:bg-bgBase text-textDefault rounded-lg font-medium transition-colors"
>
<XMarkIcon className="w-5 h-5"/>
{t('dashboard.cancel')}
</button>
<button
onClick={saveAndExit}
disabled={saving}
className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors shadow-md disabled:opacity-50"
>
{saving ? (
<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>
) : (
<CheckIcon className="w-5 h-5"/>
)}
{t('dashboard.save')}
</button>
</>
) : (
<button
onClick={enterEditMode}
className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md"
>
<PencilIcon className="w-5 h-5"/>
{t('dashboard.edit')}
</button>
)}
</div>
</div>
{designPreview && <FavoriteModulesBar editMode={editMode} inline />}
</div>
{!designPreview && <FavoriteModulesBar editMode={editMode} />}
{/* Dashboard content */}
{activeLayout.widgets.length === 0 && !editMode ? (
renderEmptyDashboard()
) : designPreview ? (
editMode ? (
<DragDropContext onDragEnd={handleDragEnd}>
<DashboardWidgetGrid
widgets={gridWidgets}
editMode
renderBody={renderWidgetBody}
onRemove={handleRemoveWidget}
onResize={handleResizeWidget}
/>
</DragDropContext>
) : (
<DashboardWidgetGrid
widgets={gridWidgets}
editMode={false}
renderBody={renderWidgetBody}
onRemove={handleRemoveWidget}
onResize={handleResizeWidget}
/>
)
) : editMode ? (
<DragDropContext onDragEnd={handleDragEnd}>
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{COLUMNS.map(col => renderColumn(col))}
</div>
</DragDropContext>
) : (
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{COLUMNS.map(col => renderColumn(col))}
</div>
)}
{designPreview && editMode && (
<button
type="button"
onClick={() => setSelectorOpen(true)}
className="mt-6 w-full border-2 border-dashed border-borderDefault rounded-xl p-4 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/5 transition-colors"
>
<PlusIcon className="w-5 h-5 mx-auto text-textTertiary"/>
<span className="mt-1 block text-sm text-textSecondary">{t('dashboard.addWidget')}</span>
<span className="mt-1 block text-xs text-textTertiary">{t('dashboard.addWidgetHint')}</span>
</button>
)}
<WidgetSelector
isOpen={selectorOpen}
onClose={() => setSelectorOpen(false)}
onSelectWidget={handleAddWidget}
existingWidgetTypes={(designPreview ? gridWidgets : activeLayout.widgets).map(w => w.widgetType)}
hiddenTypes={designPreview ? REPLACED_ON_PREVIEW : []}
extraWidgets={designPreview ? PREVIEW_ADDABLE.filter((widget) => canShowWidget(widget.type)) : []}
chooseColumn={!designPreview}
/>
{saving && (
<div className="fixed bottom-4 right-4 bg-bgElevated shadow-lg rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-textSecondary">
<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
{t('dashboard.saving')}
</div>
)}
</div>
);
};
export default Dashboard;
